import { VrboPromotionPlan } from 'models/Channels';
import {
  VrboPromotion,
  VrboPromotionStatus,
} from '../../PropertySettingsMainTab.types';

export const NEW_LISTING_DISCOUNT_DEFAULT_PERCENTAGE = 20;

type VrboPromotionFormValue = {
  uid?: string | undefined;
  type?: VrboPromotionPlan;
  percentage?: number | null;
  status?: VrboPromotionStatus;
};

export type VrboPromotionsPatchFormValues = {
  vrboSettings?: {
    promotions?: Array<VrboPromotionFormValue | null | undefined> | null;
    newListingDiscount?: boolean | string | null;
  };
};

const normalizeVrboPromotions = (
  promotions?: Array<VrboPromotionFormValue | null | undefined> | null,
): VrboPromotion[] => {
  return (promotions ?? [])
    .filter((promotion): promotion is VrboPromotionFormValue => {
      return !!promotion;
    })
    .map((promotion) => ({
      uid: promotion.uid,
      type: promotion.type!,
      percentage: promotion.percentage ?? null,
      status: promotion.status,
    }));
};

export const findNewListingDiscountPromotion = (
  promotions: VrboPromotion[] | null | undefined,
): VrboPromotion | undefined => {
  return promotions?.find(
    (promotion) => promotion.type === VrboPromotionPlan.NEW_LISTING_DISCOUNT,
  );
};

export const isNewListingDiscountFieldDisabled = (
  promotion: VrboPromotion | undefined,
): boolean => {
  if (!promotion) {
    return false;
  }

  return (
    promotion.status === VrboPromotionStatus.NOT_ELIGIBLE ||
    promotion.status === VrboPromotionStatus.DELETED ||
    promotion.status === VrboPromotionStatus.TO_BE_DELETED
  );
};

export const isNewListingDiscountEnabled = (
  promotion: VrboPromotion | undefined,
): boolean => {
  if (!promotion) {
    return false;
  }

  return (
    promotion.status === VrboPromotionStatus.SYNCED ||
    promotion.status === VrboPromotionStatus.NOT_SYNCED
  );
};

export const getNewListingDiscountFormValue = (
  promotion: VrboPromotion | undefined,
): 'true' | 'false' => {
  return isNewListingDiscountEnabled(promotion) ? 'true' : 'false';
};

export const filterPercentagePromotions = (
  promotions: VrboPromotion[] | null | undefined,
): VrboPromotion[] => {
  return (promotions ?? []).filter(
    (promotion): promotion is VrboPromotion =>
      !!promotion &&
      promotion.type !== VrboPromotionPlan.NEW_LISTING_DISCOUNT &&
      promotion.percentage != null,
  );
};

export const buildVrboPromotionsPatchPayload = (
  formValues: VrboPromotionsPatchFormValues,
): VrboPromotion[] => {
  const normalizedPromotions = normalizeVrboPromotions(
    formValues.vrboSettings?.promotions,
  );

  const promotions = filterPercentagePromotions(normalizedPromotions);

  const isNewListingDiscountEnabledInForm =
    formValues.vrboSettings?.newListingDiscount === true ||
    formValues.vrboSettings?.newListingDiscount === 'true';

  if (isNewListingDiscountEnabledInForm) {
    const newListingDiscountPromotion =
      findNewListingDiscountPromotion(normalizedPromotions);

    promotions.push({
      type: VrboPromotionPlan.NEW_LISTING_DISCOUNT,
      uid: newListingDiscountPromotion?.uid,
      percentage:
        newListingDiscountPromotion?.percentage ??
        NEW_LISTING_DISCOUNT_DEFAULT_PERCENTAGE,
    });
  }

  return promotions;
};
